import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import RadarIcon from '@mui/icons-material/Radar';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import {
  aiRecommendationsAPI,
  breakoutAPI,
  earlyAccumulationAPI,
  morningBriefAPI,
  surgeReadinessAPI,
} from '../services/api';
import { getMarketStageLabel } from '../utils/marketStageLabels';

const PAGE_BG = '#f1f5f9';

const CARD_SX = {
  border: '1px solid #e2e8f0',
  borderRadius: 2,
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  background: '#ffffff',
};

const LABEL_SX = {
  fontSize: '0.6875rem',
  fontWeight: 600,
  color: '#94a3b8',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  lineHeight: 1.2,
};

const LINK_BTN_SX = {
  textTransform: 'none',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#64748b',
  px: 0.75,
  py: 0.25,
  minWidth: 0,
  borderRadius: 1,
  '&:hover': { bgcolor: '#f8fafc', color: '#2563eb' },
};

const CHIP_NEUTRAL_SX = {
  height: 22,
  fontSize: '0.6875rem',
  fontWeight: 600,
  bgcolor: '#f8fafc',
  color: '#475569',
  border: '1px solid #e2e8f0',
  '& .MuiChip-label': { px: 0.85 },
};

const toneForStage = (stage) => {
  switch (stage) {
    case 'EXPLOSIVE_LIQUIDITY':
      return { color: '#7c3aed', bg: '#f5f3ff' };
    case 'HEALTHY_UPTREND':
      return { color: '#16a34a', bg: '#f0fdf4' };
    case 'SIDEWAY_ACCUMULATION':
      return { color: '#2563eb', bg: '#eff6ff' };
    case 'DRY_LIQUIDITY':
      return { color: '#d97706', bg: '#fff7ed' };
    case 'DISTRIBUTION':
      return { color: '#ea580c', bg: '#fff7ed' };
    case 'RISK_OFF':
      return { color: '#dc2626', bg: '#fef2f2' };
    default:
      return { color: '#64748b', bg: '#f8fafc' };
  }
};

const formatPct = (value, digits = 1, emptyLabel = '—') => {
  if (value == null || Number.isNaN(Number(value))) return emptyLabel;
  return `${Number(value).toFixed(digits)}%`;
};

const formatMonospace = (value, digits = 1, emptyLabel = '—') => {
  if (value == null || Number.isNaN(Number(value))) return emptyLabel;
  return Number(value).toLocaleString('en-US', { maximumFractionDigits: digits });
};

const toReadableLabel = (value, emptyLabel = '—') => {
  if (!value) return emptyLabel;
  return String(value)
    .replace(/^L\d+_/, '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatSector = (sector, emptyLabel = '—') => {
  if (!sector) return emptyLabel;
  const raw = typeof sector === 'string' ? sector : (sector.vi || sector.en || '');
  return toReadableLabel(raw, emptyLabel);
};

const formatRegime = (regime, t) => {
  if (!regime) return '';
  return t(`marketPhaseDashboard.regimes.${regime}`, toReadableLabel(regime, ''));
};

const formatRiskFlag = (flag, t) => t(`marketPhaseDashboard.riskFlags.${flag}`, toReadableLabel(flag, flag));

const getMarketStageReasons = (reasonCodes = [], t) => (
  reasonCodes
    .map((code) => t(`marketPhaseDashboard.reasons.${code}`, ''))
    .filter(Boolean)
);

const joinReasons = (items = [], limit = 2, t) => (
  items.filter(Boolean).slice(0, limit).join(t('marketPhaseDashboard.separators.reasonJoin'))
);

const formatSignedPctFromRatio = (value, digits = 1) => {
  if (value == null || Number.isNaN(Number(value))) return null;
  const pct = Number(value) * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(digits)}%`;
};

const getBucketEmptyWhy = (bucketType, t) => (
  t(`marketPhaseDashboard.buckets.emptyWhy.${bucketType}`, t('marketPhaseDashboard.buckets.emptyWhy.default'))
);

const getEarlyAccumulationWhy = (row, t) => {
  const reasons = [];
  const passed = Number(row?.diagnostics?.criteria_passed || 0);
  const totalChecks = (row?.diagnostics?.passed_checks || []).length + (row?.diagnostics?.failed_checks || []).length;

  if (passed && totalChecks) {
    reasons.push(t('marketPhaseDashboard.why.criteriaPassed', { passed, total: totalChecks }));
  }

  if (row?.flags?.is_breakout_trigger) {
    reasons.push(t('marketPhaseDashboard.why.breakoutTrigger'));
  } else if (row?.flags?.near_miss) {
    reasons.push(t('marketPhaseDashboard.why.nearMiss'));
  } else if (row?.flags?.is_pre_accumulation) {
    reasons.push(t('marketPhaseDashboard.why.preAccumulation'));
  }

  (row?.diagnostics?.passed_checks || []).forEach((check) => {
    const label = t(`marketPhaseDashboard.earlyChecks.${check}`, '');
    if (label) reasons.push(label);
  });

  if (typeof row?.price?.dist_from_resistance === 'number') {
    const dist = formatSignedPctFromRatio(row.price.dist_from_resistance, 1);
    if (dist) reasons.push(t('marketPhaseDashboard.why.distResistance', { dist }));
  }

  if (typeof row?.strength?.relative_strength_score === 'number') {
    reasons.push(t('marketPhaseDashboard.why.relativeStrength', { score: row.strength.relative_strength_score.toFixed(1) }));
  }

  if (typeof row?.change?.delta === 'number' && row.change.delta > 0) {
    reasons.push(t('marketPhaseDashboard.why.scoreImproving', { delta: row.change.delta.toFixed(1) }));
  }

  return reasons.slice(0, 3);
};

const getBreakoutWhy = (row, t, emptyLabel) => {
  const reasons = [];

  if (row?.trigger_type) {
    reasons.push(t('marketPhaseDashboard.why.trigger', { type: toReadableLabel(row.trigger_type, emptyLabel) }));
  }

  if (row?.signal) {
    const strength = row?.signal_strength != null ? ` (${Number(row.signal_strength).toFixed(0)})` : '';
    reasons.push(t('marketPhaseDashboard.why.signal', { signal: row.signal, strength }));
  }

  if (typeof row?.price?.dist_from_resistance === 'number') {
    const dist = formatSignedPctFromRatio(row.price.dist_from_resistance, 1);
    if (dist) reasons.push(t('marketPhaseDashboard.why.vsResistance', { dist }));
  }

  if (typeof row?.smart_money?.up_value_ratio === 'number') {
    reasons.push(t('marketPhaseDashboard.why.upDownValue', { ratio: row.smart_money.up_value_ratio.toFixed(1) }));
  }

  if (Array.isArray(row?.reasons) && row.reasons.length) {
    reasons.push(...row.reasons.slice(0, 2));
  }

  return reasons.slice(0, 3);
};

const getSurgeWhy = (row, t, emptyLabel) => {
  const reasons = [];
  if (row?.entry_signal) {
    reasons.push(t('marketPhaseDashboard.why.entrySignal', { signal: toReadableLabel(row.entry_signal, emptyLabel) }));
  } else if (row?.surge_setup_type) {
    reasons.push(t('marketPhaseDashboard.why.setup', { setup: toReadableLabel(row.surge_setup_type, emptyLabel) }));
  }

  if (Array.isArray(row?.reasons) && row.reasons.length) {
    reasons.push(...row.reasons.slice(0, 2));
  }

  if (typeof row?.entry_readiness === 'number') {
    reasons.push(t('marketPhaseDashboard.why.entryReadiness', { pct: row.entry_readiness.toFixed(0) }));
  }

  if (typeof row?.rs_vs_index === 'number') {
    const rs = formatSignedPctFromRatio(row.rs_vs_index, 1);
    if (rs) reasons.push(t('marketPhaseDashboard.why.rsVsIndex', { rs }));
  }

  return reasons.slice(0, 3);
};

const getCandidateWhy = (row, bucketType, t, emptyLabel) => {
  if (!row) return '';
  if (bucketType === 'early') return joinReasons(getEarlyAccumulationWhy(row, t), 3, t);
  if (bucketType === 'surge') return joinReasons(getSurgeWhy(row, t, emptyLabel), 3, t);
  return joinReasons(getBreakoutWhy(row, t, emptyLabel), 3, t);
};

const getBucketWhy = (rows = [], bucketType, t, emptyLabel) => {
  const primary = rows[0];
  if (!primary) return getBucketEmptyWhy(bucketType, t);
  const head = primary.ticker ? t('marketPhaseDashboard.tickerWhyHead', { ticker: primary.ticker }) : '';
  return `${head}${getCandidateWhy(primary, bucketType, t, emptyLabel)}`;
};

const getMarketAction = (stage, t) => (
  stage ? t(`marketPhaseDashboard.actions.${stage}`, '') : ''
);

const getAllocationStance = (exposure, t) => {
  if (typeof exposure !== 'number') {
    return {
      label: t('marketPhaseDashboard.stance.noData.label'),
      note: t('marketPhaseDashboard.stance.noData.note'),
      color: '#64748b',
      bg: '#f8fafc',
    };
  }
  if (exposure >= 75) {
    return {
      label: t('marketPhaseDashboard.stance.offense.label'),
      note: t('marketPhaseDashboard.stance.offense.note'),
      color: '#16a34a',
      bg: '#f0fdf4',
    };
  }
  if (exposure >= 55) {
    return {
      label: t('marketPhaseDashboard.stance.balance.label'),
      note: t('marketPhaseDashboard.stance.balance.note'),
      color: '#2563eb',
      bg: '#eff6ff',
    };
  }
  if (exposure >= 35) {
    return {
      label: t('marketPhaseDashboard.stance.selective.label'),
      note: t('marketPhaseDashboard.stance.selective.note'),
      color: '#d97706',
      bg: '#fff7ed',
    };
  }
  return {
    label: t('marketPhaseDashboard.stance.defensive.label'),
    note: t('marketPhaseDashboard.stance.defensive.note'),
    color: '#dc2626',
    bg: '#fef2f2',
  };
};

const buildHighConviction = (recommendations) => {
  const source = recommendations || {};
  const map = {};
  Object.entries(source).forEach(([style, styleData]) => {
    const rows = styleData?.results || [];
    rows.forEach((row) => {
      if (!map[row.ticker]) {
        map[row.ticker] = {
          ticker: row.ticker,
          companyName: row.companyName || row.company_name,
          sector: row.sector,
          count: 0,
          scores: [],
          styles: [],
          reasons: [],
          marketStageOverlays: [],
        };
      }
      map[row.ticker].count += 1;
      map[row.ticker].styles.push(style);
      if (row.score != null) map[row.ticker].scores.push(Number(row.score));
      if (Array.isArray(row.reasons)) {
        row.reasons.forEach((reason) => {
          if (reason && !map[row.ticker].reasons.includes(reason)) map[row.ticker].reasons.push(reason);
        });
      }
      if (row.marketStageOverlay) {
        map[row.ticker].marketStageOverlays.push(row.marketStageOverlay);
      }
    });
  });
  return Object.values(map)
    .map((row) => ({
      ...row,
      avgScore: row.scores.length ? row.scores.reduce((a, b) => a + b, 0) / row.scores.length : null,
    }))
    .filter((row) => row.count >= 2)
    .sort((a, b) => b.count - a.count || (b.avgScore || 0) - (a.avgScore || 0))
    .slice(0, 8);
};

function SectionCard({ title, action, children, sx = {}, contentSx = {}, noPadding = false }) {
  return (
    <Paper elevation={0} sx={{ ...CARD_SX, height: '100%', display: 'flex', flexDirection: 'column', ...sx }}>
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: '1px solid #f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: '#1e293b', letterSpacing: -0.1 }}>
          {title}
        </Typography>
        {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
      </Box>
      <Box sx={{ ...(noPadding ? {} : { p: 1.5 }), flex: 1, minHeight: 0, ...contentSx }}>{children}</Box>
    </Paper>
  );
}

function StatBar({ items }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))`,
        borderRadius: 1.5,
        border: '1px solid #f1f5f9',
        bgcolor: '#f8fafc',
        overflow: 'hidden',
      }}
    >
      {items.map((item, index) => (
        <Box
          key={item.label}
          sx={{
            px: 1,
            py: 0.85,
            textAlign: 'center',
            borderLeft: index > 0 ? '1px solid #e2e8f0' : 'none',
          }}
        >
          <Typography sx={LABEL_SX}>{item.label}</Typography>
          <Typography
            sx={{
              mt: 0.35,
              fontSize: '1rem',
              fontWeight: 700,
              color: item.tone || '#1e293b',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1,
            }}
          >
            {item.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function SectorTable({ leadingTitle, laggingTitle, leading = [], lagging = [], emptyLabel = '—' }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 0,
        borderRadius: 1.5,
        border: '1px solid #f1f5f9',
        overflow: 'hidden',
      }}
    >
      {[
        { title: leadingTitle, items: leading, tone: '#16a34a' },
        { title: laggingTitle, items: lagging, tone: '#dc2626' },
      ].map((col, colIndex) => (
        <Box key={col.title} sx={{ borderLeft: colIndex > 0 ? '1px solid #f1f5f9' : 'none' }}>
          <Box sx={{ px: 1, py: 0.55, bgcolor: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
            <Typography sx={{ ...LABEL_SX, color: col.tone }}>{col.title}</Typography>
          </Box>
          {(col.items || []).slice(0, 3).map((item) => (
            <Box
              key={item.sector}
              sx={{
                px: 1,
                py: 0.5,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 0.5,
                borderBottom: '1px solid #f8fafc',
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <Typography sx={{ fontSize: '0.75rem', color: '#475569', lineHeight: 1.2 }} noWrap>
                {formatSector(item.sector, emptyLabel)}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: col.tone, fontFamily: 'monospace', flexShrink: 0 }}>
                {item.rs?.toFixed(1) ?? emptyLabel}
              </Typography>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

function OpportunityColumn({
  title,
  accent,
  whyText,
  rows,
  emptyText,
  onTickerClick,
  scoreKey = 'score',
  emptyLabel = '—',
}) {
  const visibleRows = rows.slice(0, 3);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 168,
        borderRight: '1px solid #f1f5f9',
        '&:last-child': { borderRight: 'none' },
      }}
    >
      <Box
        sx={{
          px: 1.15,
          py: 0.85,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.5,
          borderBottom: '1px solid #f8fafc',
          bgcolor: '#fafbfc',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65, minWidth: 0 }}>
          <Box sx={{ width: 3, height: 16, borderRadius: 1, bgcolor: accent, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }} noWrap>
            {title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
          {whyText ? (
            <Tooltip title={whyText} arrow placement="top">
              <InfoOutlinedIcon sx={{ fontSize: 15, color: '#94a3b8', cursor: 'help' }} />
            </Tooltip>
          ) : null}
          <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: '#94a3b8', fontFamily: 'monospace', minWidth: 14, textAlign: 'right' }}>
            {visibleRows.length}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1 }}>
        {visibleRows.length ? (
          visibleRows.map((row) => {
            const score = formatMonospace(
              row[scoreKey] ?? row.breakout_score ?? row.early_accumulation_score ?? row.opportunity_score ?? row.score,
              1,
              emptyLabel
            );
            return (
              <Tooltip key={row.ticker} title={row.whyText || ''} arrow placement="left" disableHoverListener={!row.whyText}>
                <Box
                  onClick={() => onTickerClick?.(row.ticker)}
                  sx={{
                    px: 1.15,
                    py: 0.65,
                    display: 'grid',
                    gridTemplateColumns: '48px minmax(0, 1fr) auto',
                    gap: 0.5,
                    alignItems: 'center',
                    cursor: onTickerClick ? 'pointer' : 'default',
                    borderBottom: '1px solid #f8fafc',
                    '&:hover': onTickerClick ? { bgcolor: '#f8fafc' } : {},
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: '#1e293b' }}>{row.ticker}</Typography>
                  <Typography sx={{ fontSize: '0.6875rem', color: '#94a3b8', lineHeight: 1.2 }} noWrap>
                    {formatSector(row.sector || row.sector_name || row.industry_name, emptyLabel)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: accent, fontFamily: 'monospace' }}>
                    {score}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })
        ) : (
          <Box sx={{ px: 1.15, py: 2, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.75rem', color: '#cbd5e1', lineHeight: 1.4 }}>{emptyText}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function WatchlistRow({ row, onClick, stylesLabel, emptyLabel = '—' }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'grid',
        gridTemplateColumns: '52px minmax(0, 1fr) auto auto',
        gap: 0.75,
        alignItems: 'center',
        px: 1.25,
        py: 0.7,
        cursor: 'pointer',
        borderBottom: '1px solid #f8fafc',
        '&:hover': { bgcolor: '#f8fafc' },
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: '#1e293b' }}>{row.ticker}</Typography>
      <Typography
        sx={{
          fontSize: '0.6875rem',
          color: '#64748b',
          lineHeight: 1.3,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {row.companyName || formatSector(row.sector, emptyLabel)}
      </Typography>
      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: '#94a3b8' }}>{row.count} {stylesLabel}</Typography>
      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: '#2563eb', fontFamily: 'monospace' }}>
        {row.avgScore != null ? row.avgScore.toFixed(1) : emptyLabel}
      </Typography>
    </Box>
  );
}

export default function MarketPhaseDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language?.startsWith('vi') ? 'vi' : 'en';
  const emDash = t('marketPhaseDashboard.emDash');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [payload, setPayload] = useState({
    brief: null,
    recommendations: {},
    preparation: [],
    breakout: [],
    earlyAccumulation: [],
    surgeReadiness: [],
  });

  const load = useCallback(async () => {
    setError('');
    setRefreshing(true);
    try {
      const results = await Promise.allSettled([
        morningBriefAPI.get(),
        aiRecommendationsAPI.getAllRecommendations(20),
        breakoutAPI.getCandidates({ preset: 'preparation_phase', min_score: 70, limit: 6, exclude_trigger_none: false }),
        breakoutAPI.getCandidates({ preset: 'balanced', min_score: 75, limit: 6, strong_only: true, exclude_trigger_none: true }),
        earlyAccumulationAPI.getCandidates({
          stage: 'MATURE,BREAKOUT,NEAR_MISS',
          early_only: false,
          near_miss_only: false,
          forming_only: false,
          min_score: 60,
          strong_only: true,
          limit: 6,
        }),
        surgeReadinessAPI.getLatest({ min_score: 72, limit: 6 }),
      ]);

      const [briefRes, recoRes, prepRes, breakoutRes, earlyRes, surgeRes] = results;
      const brief = briefRes.status === 'fulfilled' ? briefRes.value.data?.data || null : null;
      const recommendations = recoRes.status === 'fulfilled' ? recoRes.value.data?.recommendations || {} : {};
      const preparation = prepRes.status === 'fulfilled' ? prepRes.value.data?.candidates || [] : [];
      const breakout = breakoutRes.status === 'fulfilled' ? breakoutRes.value.data?.candidates || [] : [];
      const earlyAccumulation = earlyRes.status === 'fulfilled' ? earlyRes.value.data?.candidates || [] : [];
      const surgeReadiness = surgeRes.status === 'fulfilled' ? surgeRes.value.data?.candidates || [] : [];

      if (!brief && !Object.keys(recommendations).length) {
        throw new Error(t('marketPhaseDashboard.errors.noData'));
      }

      setPayload({ brief, recommendations, preparation, breakout, earlyAccumulation, surgeReadiness });
    } catch (err) {
      setError(err?.message || t('marketPhaseDashboard.errors.loadFailed'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const marketStage = payload.brief?.market_stage || null;
  const allocation = payload.brief?.allocation || null;
  const sectors = payload.brief?.sectors || {};
  const sentiment = payload.brief?.sentiment || null;
  const marketRisk = payload.brief?.market_risk || null;
  const topPicks = useMemo(() => payload.brief?.top_picks || [], [payload.brief?.top_picks]);
  const exposure = marketStage?.exposure_target_pct ?? (
    sentiment?.exposure_target != null ? sentiment.exposure_target * 100 : null
  );
  const stance = getAllocationStance(typeof exposure === 'number' ? exposure : null, t);
  const highConviction = useMemo(() => buildHighConviction(payload.recommendations), [payload.recommendations]);
  const detectorFocus = useMemo(() => topPicks.slice(0, 6), [topPicks]);
  const topStyles = useMemo(
    () => Object.entries(payload.recommendations || {})
      .map(([style, data]) => ({ style, count: data?.results?.length || 0, name: data?.name?.[lang] || style.replace(/_/g, ' ') }))
      .filter((row) => row.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 4),
    [lang, payload.recommendations]
  );
  const marketStageReasons = useMemo(
    () => getMarketStageReasons(marketStage?.reason_codes || [], t).slice(0, 4),
    [marketStage?.reason_codes, t]
  );
  const preparationRows = useMemo(
    () => payload.preparation.slice(0, 3).map((row) => ({
      ...row,
      score: row.breakout_score,
      whyText: getCandidateWhy(row, 'preparation', t, emDash),
    })),
    [payload.preparation, t, emDash]
  );
  const earlyRows = useMemo(
    () => payload.earlyAccumulation.slice(0, 3).map((row) => ({
      ...row,
      score: row.early_accumulation_score,
      sector: row.sector_name?.[lang] || row.sector_name?.vi || row.sector,
      whyText: getCandidateWhy(row, 'early', t, emDash),
    })),
    [payload.earlyAccumulation, lang, t, emDash]
  );
  const breakoutRows = useMemo(
    () => payload.breakout.slice(0, 3).map((row) => ({
      ...row,
      score: row.breakout_score,
      whyText: getCandidateWhy(row, 'breakout', t, emDash),
    })),
    [payload.breakout, t, emDash]
  );
  const surgeRows = useMemo(
    () => payload.surgeReadiness.slice(0, 3).map((row) => ({
      ...row,
      score: row.opportunity_score,
      sector: row.sector,
      whyText: getCandidateWhy(row, 'surge', t, emDash),
    })),
    [payload.surgeReadiness, t, emDash]
  );

  if (loading) {
    return (
      <Box sx={{ bgcolor: PAGE_BG, minHeight: '100%', p: { xs: 1.25, md: 1.5 } }}>
        <Box sx={{ maxWidth: 1680, mx: 'auto' }}>
          <Stack spacing={1.5}>
            <Skeleton variant="rounded" height={52} sx={{ borderRadius: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.5fr 1fr' }, gap: 1.5 }}>
              <Skeleton variant="rounded" height={220} sx={{ borderRadius: 2 }} />
              <Skeleton variant="rounded" height={220} sx={{ borderRadius: 2 }} />
            </Box>
            <Skeleton variant="rounded" height={240} sx={{ borderRadius: 2 }} />
          </Stack>
        </Box>
      </Box>
    );
  }

  const stageTone = marketStage ? toneForStage(marketStage.stage) : null;

  return (
    <Box sx={{ bgcolor: PAGE_BG, minHeight: '100%', pb: 2 }}>
      <Box sx={{ maxWidth: 1680, mx: 'auto', p: { xs: 1.25, md: 1.5 } }}>
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, minWidth: 0 }}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1.25,
                  flexShrink: 0,
                  background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 3px 10px rgba(99, 102, 241, 0.18)',
                }}
              >
                <RadarIcon sx={{ fontSize: 20, color: '#fff' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: { xs: '1.05rem', sm: '1.15rem' }, fontWeight: 800, color: '#1e293b', letterSpacing: -0.2, lineHeight: 1.15 }}>
                    {t('marketPhaseDashboard.title')}
                  </Typography>
                  {payload.brief?.allocation?.trade_date ? (
                    <Chip size="small" label={payload.brief.allocation.trade_date} sx={{ ...CHIP_NEUTRAL_SX }} />
                  ) : null}
                </Box>
                <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: 0.2, lineHeight: 1.35, maxWidth: 640 }}>
                  {t('marketPhaseDashboard.subtitle')}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={load}
              startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', fontSize: 12.5, fontWeight: 600, borderColor: '#e2e8f0', color: '#475569', bgcolor: '#fff' }}
            >
              {t('marketPhaseDashboard.refresh')}
            </Button>
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.5fr) minmax(300px, 1fr)' },
              gap: 1.5,
              alignItems: 'stretch',
            }}
          >
            <SectionCard
              title={t('marketPhaseDashboard.phaseTitle')}
              action={(
                <Button size="small" endIcon={<ChevronRightIcon sx={{ fontSize: 16 }} />} onClick={() => navigate('/sector-rotation')} sx={LINK_BTN_SX}>
                  {t('marketPhaseDashboard.viewSectorRotation')}
                </Button>
              )}
            >
              {marketStage ? (
                <Stack spacing={1.15}>
                  <Box sx={{ borderLeft: `3px solid ${stageTone.color}`, pl: 1.25 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.65 }}>
                      <Chip
                        label={getMarketStageLabel(t, marketStage.stage)}
                        sx={{ ...CHIP_NEUTRAL_SX, color: stageTone.color, borderColor: alpha(stageTone.color, 0.25) }}
                      />
                      {allocation?.regime ? (
                        <Chip label={formatRegime(allocation.regime, t)} sx={CHIP_NEUTRAL_SX} />
                      ) : null}
                      {typeof exposure === 'number' ? (
                        <Chip label={t('marketPhaseDashboard.targetEquityChip', { pct: formatPct(exposure, 0, emDash) })} sx={CHIP_NEUTRAL_SX} />
                      ) : null}
                    </Box>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: '#1e293b', lineHeight: 1.45 }}>
                      {getMarketAction(marketStage.stage, t)}
                    </Typography>
                    {marketStageReasons.length ? (
                      <Typography sx={{ mt: 0.55, fontSize: '0.75rem', color: '#64748b', lineHeight: 1.45 }}>
                        {marketStageReasons.join(t('marketPhaseDashboard.separators.reasonJoin'))}
                      </Typography>
                    ) : null}
                    {Array.isArray(marketRisk?.active_flags) && marketRisk.active_flags.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.55 }}>
                        {marketRisk.active_flags.slice(0, 4).map((flag) => (
                          <Chip key={flag} size="small" label={formatRiskFlag(flag, t)} sx={{ ...CHIP_NEUTRAL_SX, color: '#dc2626', borderColor: '#fecaca' }} />
                        ))}
                      </Box>
                    ) : null}
                  </Box>

                  <StatBar
                    items={[
                      {
                        label: t('marketPhaseDashboard.marketHealth'),
                        value: typeof marketStage.market_health_score === 'number' ? marketStage.market_health_score.toFixed(0) : emDash,
                        tone: stageTone.color,
                      },
                      {
                        label: t('marketPhaseDashboard.confidence'),
                        value: typeof marketStage.confidence_score === 'number' ? marketStage.confidence_score.toFixed(0) : emDash,
                      },
                      {
                        label: t('marketPhaseDashboard.sentiment'),
                        value: sentiment?.msi != null ? sentiment.msi.toFixed(0) : emDash,
                      },
                      {
                        label: t('marketPhaseDashboard.riskScore'),
                        value: marketRisk?.risk_score != null ? marketRisk.risk_score.toFixed(0) : emDash,
                        tone: marketRisk?.alert_level === 'HIGH' || marketRisk?.alert_level === 'EXTREME' ? '#dc2626' : '#1e293b',
                      },
                    ]}
                  />

                  <SectorTable
                    leadingTitle={t('marketPhaseDashboard.leadingSectors')}
                    laggingTitle={t('marketPhaseDashboard.laggingSectors')}
                    leading={sectors.leading}
                    lagging={sectors.lagging}
                    emptyLabel={emDash}
                  />
                </Stack>
              ) : (
                <Typography sx={{ fontSize: '0.8125rem', color: '#94a3b8' }}>
                  {t('marketPhaseDashboard.noPhase')}
                </Typography>
              )}
            </SectionCard>

            <SectionCard
              title={t('marketPhaseDashboard.capitalTitle')}
              action={(
                <Button size="small" endIcon={<ChevronRightIcon sx={{ fontSize: 16 }} />} onClick={() => navigate('/concentration')} sx={LINK_BTN_SX}>
                  {t('marketPhaseDashboard.viewAllocation')}
                </Button>
              )}
            >
              <Stack spacing={1.15}>
                <Box sx={{ borderLeft: `3px solid ${stance.color}`, pl: 1.15, py: 0.15 }}>
                  <Typography sx={{ ...LABEL_SX, color: stance.color }}>{stance.label}</Typography>
                  <Typography sx={{ mt: 0.35, fontSize: '0.8125rem', color: '#475569', lineHeight: 1.45 }}>
                    {stance.note}
                  </Typography>
                </Box>

                <StatBar
                  items={[
                    { label: t('marketPhaseDashboard.targetEquity'), value: typeof exposure === 'number' ? formatPct(exposure, 0, emDash) : emDash },
                    { label: t('marketPhaseDashboard.currentLong'), value: allocation?.long_pct != null ? formatPct(allocation.long_pct, 0, emDash) : emDash },
                    { label: t('marketPhaseDashboard.cashReserve'), value: allocation?.cash_pct != null ? formatPct(allocation.cash_pct, 0, emDash) : emDash },
                  ]}
                />

                {allocation?.gates ? (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {[
                      { key: 'bluechip_open', label: t('marketPhaseDashboard.bluechipGate') },
                      { key: 'midcap_open', label: t('marketPhaseDashboard.midcapGate') },
                    ].map((gate) => (
                      <Chip
                        key={gate.key}
                        size="small"
                        label={t('marketPhaseDashboard.gateStatus', {
                          gate: gate.label,
                          status: allocation.gates[gate.key] ? t('marketPhaseDashboard.open') : t('marketPhaseDashboard.tight'),
                        })}
                        sx={{
                          ...CHIP_NEUTRAL_SX,
                          color: allocation.gates[gate.key] ? '#15803d' : '#64748b',
                          borderColor: allocation.gates[gate.key] ? '#bbf7d0' : '#e2e8f0',
                        }}
                      />
                    ))}
                  </Box>
                ) : null}

                {highConviction.length ? (
                  <Box>
                    <Typography sx={{ ...LABEL_SX, mb: 0.55 }}>
                      {t('marketPhaseDashboard.focusTickers')}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {highConviction.slice(0, 6).map((row) => (
                        <Chip
                          key={row.ticker}
                          label={row.ticker}
                          onClick={() => navigate(`/stocks/${row.ticker}`)}
                          sx={{ ...CHIP_NEUTRAL_SX, cursor: 'pointer', fontWeight: 700, color: '#1e293b' }}
                        />
                      ))}
                    </Stack>
                  </Box>
                ) : null}

                {topStyles.length ? (
                  <Box>
                    <Typography sx={{ ...LABEL_SX, mb: 0.55 }}>
                      {t('marketPhaseDashboard.priorityScanners')}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {topStyles.map((row) => (
                        <Chip key={row.style} label={t('marketPhaseDashboard.styleCount', { name: row.name, count: row.count })} sx={CHIP_NEUTRAL_SX} />
                      ))}
                    </Stack>
                  </Box>
                ) : null}
              </Stack>
            </SectionCard>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1fr) minmax(300px, 340px)' },
              gap: 1.5,
              alignItems: 'stretch',
            }}
          >
            <SectionCard
              title={t('marketPhaseDashboard.opportunityBoard')}
              noPadding
              contentSx={{ p: 0 }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
                }}
              >
                <OpportunityColumn
                  title={t('marketPhaseDashboard.preparationTitle')}
                  accent="#2563eb"
                  whyText={getBucketWhy(preparationRows, 'preparation', t, emDash)}
                  rows={preparationRows}
                  emptyText={t('marketPhaseDashboard.noPreparation')}
                  onTickerClick={(ticker) => navigate(`/stocks/${ticker}`)}
                  emptyLabel={emDash}
                />
                <OpportunityColumn
                  title={t('marketPhaseDashboard.earlyTitle')}
                  accent="#15803d"
                  whyText={getBucketWhy(earlyRows, 'early', t, emDash)}
                  rows={earlyRows}
                  emptyText={t('marketPhaseDashboard.noEarly')}
                  onTickerClick={(ticker) => navigate(`/stocks/${ticker}`)}
                  emptyLabel={emDash}
                />
                <OpportunityColumn
                  title={t('marketPhaseDashboard.breakoutTitle')}
                  accent="#ea580c"
                  whyText={getBucketWhy(breakoutRows, 'breakout', t, emDash)}
                  rows={breakoutRows}
                  emptyText={t('marketPhaseDashboard.noBreakout')}
                  onTickerClick={(ticker) => navigate(`/stocks/${ticker}`)}
                  emptyLabel={emDash}
                />
                <OpportunityColumn
                  title={t('marketPhaseDashboard.surgeTitle')}
                  accent="#7c3aed"
                  whyText={getBucketWhy(surgeRows, 'surge', t, emDash)}
                  rows={surgeRows}
                  emptyText={t('marketPhaseDashboard.noSurge')}
                  onTickerClick={(ticker) => navigate(`/stocks/${ticker}`)}
                  scoreKey="score"
                  emptyLabel={emDash}
                />
              </Box>
            </SectionCard>

            <SectionCard
              title={t('marketPhaseDashboard.watchTitle')}
              noPadding
              action={(
                <Button size="small" endIcon={<ChevronRightIcon sx={{ fontSize: 16 }} />} onClick={() => navigate('/recommendations')} sx={LINK_BTN_SX}>
                  {t('marketPhaseDashboard.viewRecommendations')}
                </Button>
              )}
              contentSx={{ p: 0, display: 'flex', flexDirection: 'column' }}
            >
              {highConviction.length ? (
                <Box sx={{ flex: 1 }}>
                  {highConviction.slice(0, 6).map((row) => (
                    <WatchlistRow
                      key={row.ticker}
                      row={row}
                      stylesLabel={t('marketPhaseDashboard.styles')}
                      emptyLabel={emDash}
                      onClick={() => navigate(`/stocks/${row.ticker}`)}
                    />
                  ))}
                </Box>
              ) : (
                <Box sx={{ px: 1.5, py: 2.5, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '0.8125rem', color: '#94a3b8' }}>
                    {t('marketPhaseDashboard.noWatchlist')}
                  </Typography>
                </Box>
              )}

              {detectorFocus.length ? (
                <Box sx={{ px: 1.25, py: 1, borderTop: '1px solid #f1f5f9', bgcolor: '#fafbfc' }}>
                  <Typography sx={{ ...LABEL_SX, mb: 0.55 }}>
                    {t('marketPhaseDashboard.detectorPicks')}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {detectorFocus.map((item) => (
                      <Tooltip key={item.ticker} title={typeof item.reason === 'string' ? item.reason : (item.note || '')} arrow>
                        <Chip
                          label={`${item.ticker}`}
                          onClick={() => navigate(`/stocks/${item.ticker}`)}
                          sx={{ ...CHIP_NEUTRAL_SX, cursor: 'pointer', fontWeight: 700 }}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>
              ) : null}
            </SectionCard>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
